/**
 * Rybbit Event Tracking Utility
 *
 * All events automatically include attribution data (UTM params, referrer)
 * for source-to-conversion tracking.
 */

import { getAttribution } from './attribution';

// Map short keys to event property names
const ATTR_KEY_MAP: Record<string, string> = {
  src: 'attr_source',
  med: 'attr_medium',
  cmp: 'attr_campaign',
  trm: 'attr_term',
  cnt: 'attr_content',
  ref: 'attr_referrer',
};

/**
 * Track a custom event to Rybbit with automatic attribution injection
 */
const trackRybbitEvent = (
  name: string,
  properties?: Record<string, string | number | boolean | undefined>,
  skipAttribution = false
): void => {
  if (typeof window === 'undefined' || !window.rybbit) return;

  try {
    const sanitizedProps: Record<string, string | number> = {};

    // Add attribution props (cached in memory, no storage reads)
    if (!skipAttribution) {
      const attr = getAttribution();
      for (const [key, value] of Object.entries(attr)) {
        const mappedKey = ATTR_KEY_MAP[key] || `attr_${key}`;
        sanitizedProps[mappedKey] = value;
      }
    }

    // Add custom properties
    if (properties) {
      for (const [key, value] of Object.entries(properties)) {
        if (value === undefined) continue;
        sanitizedProps[key] = typeof value === 'string' || typeof value === 'number' ? value : String(value);
      }
    }

    // INP FIX: Defer analytics to next task so user interactions paint first
    const props = Object.keys(sanitizedProps).length > 0 ? sanitizedProps : undefined;
    setTimeout(() => {
      try {
        window.rybbit?.event(name, props);
      } catch { /* tracking failures shouldn't break the app */ }
    }, 0);

    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('[Rybbit] Event tracked:', name, sanitizedProps);
    }
  } catch (error) {
    console.error('[Rybbit] Failed to track event:', name, error);
  }
};

/**
 * Track signup event
 */
export const trackRybbitSignup = (method: 'google' | 'email' | 'magic_link') => {
  trackRybbitEvent('signup', { method });
};

/**
 * Track pricing page view
 */
export const trackRybbitPricingView = (params?: {
  from_location?: string;
  user_type?: 'anonymous' | 'signed_in' | 'premium';
}) => {
  trackRybbitEvent('view_pricing', params);
};

/**
 * Track checkout initiation
 */
export const trackRybbitBeginCheckout = (params: {
  plan_type: 'weekly' | 'monthly' | 'lifetime';
  value: number;
  from_location?: string;
  character_limit_reached?: boolean;
}) => {
  trackRybbitEvent('begin_checkout', {
    plan_type: params.plan_type,
    value: params.value,
    currency: 'USD',
    from_location: params.from_location,
    character_limit_reached: params.character_limit_reached ? 'true' : 'false',
  });
};

/**
 * Track character limit reached
 */
export const trackRybbitCharacterLimitReached = (params: {
  current_limit: number;
  text_length: number;
  user_type: 'anonymous' | 'signed_in';
}) => {
  trackRybbitEvent('character_limit_reached', params);
};

/**
 * Track watermark detection
 */
export const trackRybbitWatermarkDetection = (params: {
  text_length: number;
  watermarks_found: number;
  user_type: 'free' | 'premium';
  subscription_type?: string;
}) => {
  trackRybbitEvent('watermark_detected', {
    text_length: params.text_length,
    watermarks_found: params.watermarks_found,
    user_type: params.user_type,
    subscription_type: params.subscription_type,
  });
};

/**
 * Track watermark removal
 */
export const trackRybbitWatermarkRemoval = (params: {
  text_length: number;
  user_type: 'free' | 'premium';
  subscription_type?: string;
  dash_handling_mode?: 'none' | 'remove' | 'comma' | 'custom';
}) => {
  trackRybbitEvent('watermark_removed', {
    text_length: params.text_length,
    user_type: params.user_type,
    subscription_type: params.subscription_type,
    dash_handling_mode: params.dash_handling_mode || 'none',
  });
};

/**
 * Track text copy action
 */
export const trackRybbitTextCopy = (params: {
  type: 'original' | 'cleaned';
  length: number;
  user_type: 'free' | 'premium';
  subscription_type?: string;
}) => {
  trackRybbitEvent('copy_text', {
    text_type: params.type,
    text_length: params.length,
    user_type: params.user_type,
    subscription_type: params.subscription_type,
  });
};

/**
 * Track document upload
 */
export const trackRybbitDocxUpload = (params: {
  file_size_kb: number;
  user_type: 'free' | 'premium';
  subscription_type?: string;
}) => {
  trackRybbitEvent('docx_upload', {
    file_size_kb: params.file_size_kb,
    user_type: params.user_type,
    subscription_type: params.subscription_type,
  });
};

/**
 * Track successful document cleaning
 */
export const trackRybbitDocxCleanSuccess = (params: {
  file_size_kb: number;
  processing_time_seconds: number;
  user_type: 'free' | 'premium';
  subscription_type?: string;
}) => {
  trackRybbitEvent('docx_clean_success', {
    file_size_kb: params.file_size_kb,
    processing_time_seconds: params.processing_time_seconds,
    user_type: params.user_type,
    subscription_type: params.subscription_type,
  });
};

/**
 * Track document download
 */
export const trackRybbitDocxDownload = (params: {
  file_size_kb: number;
  user_type: 'free' | 'premium';
  subscription_type?: string;
}) => {
  trackRybbitEvent('docx_download', {
    file_size_kb: params.file_size_kb,
    user_type: params.user_type,
    subscription_type: params.subscription_type,
  });
};

/**
 * Track login
 */
export const trackRybbitLogin = (method: 'google' | 'email' | 'magic_link') => {
  trackRybbitEvent('login', { method });
};

/**
 * Track logout
 */
export const trackRybbitLogout = () => {
  trackRybbitEvent('logout');
};

/**
 * Track cooldown encountered
 */
export const trackRybbitCooldownEncountered = (params: {
  remaining_seconds: number;
}) => {
  trackRybbitEvent('cooldown_encountered', {
    remaining_seconds: params.remaining_seconds,
  });
};

/**
 * Track example loaded
 */
export const trackRybbitExampleLoaded = () => {
  trackRybbitEvent('example_loaded');
};

/**
 * Track FAQ interaction - Generic
 */
export const trackRybbitFaqInteraction = (params: {
  question_topic: string;
}) => {
  trackRybbitEvent('faq_interaction', {
    question_topic: params.question_topic,
  });
};

/**
 * Track FAQ: What are AI watermarks?
 */
export const trackRybbitFaqAiWatermarks = () => {
  trackRybbitEvent('faq_ai_watermarks');
};

/**
 * Track FAQ: How does the watermark removal tool work?
 */
export const trackRybbitFaqHowItWorks = () => {
  trackRybbitEvent('faq_how_it_works');
};

/**
 * Track FAQ: Is my text stored or logged?
 */
export const trackRybbitFaqPrivacy = () => {
  trackRybbitEvent('faq_privacy');
};

/**
 * Track FAQ: What types of watermarks can be detected?
 */
export const trackRybbitFaqWatermarkTypes = () => {
  trackRybbitEvent('faq_watermark_types');
};

/**
 * Track FAQ: Will removing watermarks affect my text?
 */
export const trackRybbitFaqTextAffect = () => {
  trackRybbitEvent('faq_text_affect');
};

/**
 * Track FAQ: Is there a character limit?
 */
export const trackRybbitFaqCharacterLimit = () => {
  trackRybbitEvent('faq_character_limit');
};

/**
 * Track FAQ: Can I clean watermarks from Word documents?
 */
export const trackRybbitFaqWordDocuments = () => {
  trackRybbitEvent('faq_word_documents');
};

/**
 * Track FAQ: Can I clean watermarks from Apple Pages documents?
 */
export const trackRybbitFaqPagesDocuments = () => {
  trackRybbitEvent('faq_pages_documents');
};

/**
 * Track FAQ: How secure is document processing?
 */
export const trackRybbitFaqDocumentSecurity = () => {
  trackRybbitEvent('faq_document_security');
};

/**
 * Track FAQ: Why does my cleaned document still show watermarks?
 */
export const trackRybbitFaqCopyPasteWatermarks = () => {
  trackRybbitEvent('faq_copy_paste_watermarks');
};

/**
 * Track navigation click
 */
export const trackRybbitNavigationClick = (params: {
  from: string;
  to: string;
  link_text: string;
}) => {
  trackRybbitEvent('navigation_click', {
    from: params.from,
    to: params.to,
    link_text: params.link_text,
  });
};

/**
 * Track outbound link click
 */
export const trackRybbitOutboundLinkClick = (params: {
  url: string;
  link_text: string;
}) => {
  trackRybbitEvent('outbound_link_click', {
    url: params.url,
    link_text: params.link_text,
  });
};

/**
 * Track language change
 */
export const trackRybbitLanguageChanged = (params: {
  from_language: string;
  to_language: string;
}) => {
  trackRybbitEvent('language_changed', {
    from_language: params.from_language,
    to_language: params.to_language,
  });
};

/**
 * Track theme toggle
 */
export const trackRybbitThemeToggled = (params: {
  new_theme: 'light' | 'dark';
}) => {
  trackRybbitEvent('theme_toggled', {
    new_theme: params.new_theme,
  });
};

/**
 * Track DOCX clean error
 */
export const trackRybbitDocxCleanError = (params: {
  error_message: string;
  file_size_kb: number;
  user_type: 'free' | 'premium';
}) => {
  trackRybbitEvent('docx_clean_error', {
    error_message: params.error_message,
    file_size_kb: params.file_size_kb,
    user_type: params.user_type,
  });
};

/**
 * Track Apple Pages upload
 */
export const trackRybbitPagesUpload = (params: {
  file_size_kb: number;
  user_type: 'free' | 'premium';
  subscription_type?: string;
}) => {
  trackRybbitEvent('pages_upload', {
    file_size_kb: params.file_size_kb,
    user_type: params.user_type,
    subscription_type: params.subscription_type,
  });
};

/**
 * Track successful Pages document cleaning
 */
export const trackRybbitPagesCleanSuccess = (params: {
  file_size_kb: number;
  processing_time_seconds: number;
  user_type: 'free' | 'premium';
  subscription_type?: string;
}) => {
  trackRybbitEvent('pages_clean_success', {
    file_size_kb: params.file_size_kb,
    processing_time_seconds: params.processing_time_seconds,
    user_type: params.user_type,
    subscription_type: params.subscription_type,
  });
};

/**
 * Track Pages document download
 */
export const trackRybbitPagesDownload = (params: {
  file_size_kb: number;
  user_type: 'free' | 'premium';
  subscription_type?: string;
}) => {
  trackRybbitEvent('pages_download', {
    file_size_kb: params.file_size_kb,
    user_type: params.user_type,
    subscription_type: params.subscription_type,
  });
};

/**
 * Track Pages clean error
 */
export const trackRybbitPagesCleanError = (params: {
  error_message: string;
  file_size_kb: number;
  user_type: 'free' | 'premium';
}) => {
  trackRybbitEvent('pages_clean_error', {
    error_message: params.error_message,
    file_size_kb: params.file_size_kb,
    user_type: params.user_type,
  });
};

/**
 * Track DOCX login required prompt
 */
export const trackRybbitDocxLoginRequired = () => {
  trackRybbitEvent('docx_login_required');
};

/**
 * Track DOCX login button click
 */
export const trackRybbitDocxLoginClick = () => {
  trackRybbitEvent('docx_login_click');
};

/**
 * Track Pages login required prompt
 */
export const trackRybbitPagesLoginRequired = () => {
  trackRybbitEvent('pages_login_required');
};

/**
 * Track Pages login button click
 */
export const trackRybbitPagesLoginClick = () => {
  trackRybbitEvent('pages_login_click');
};

/**
 * Track feature request submission
 */
export const trackRybbitFeatureRequestSubmitted = (params: {
  text_length: number;
  user_type: 'free' | 'premium';
}) => {
  trackRybbitEvent('feature_request_submitted', {
    text_length: params.text_length,
    user_type: params.user_type,
  });
};

/**
 * Track subscription cancellation
 */
export const trackRybbitSubscriptionCancelled = (params: {
  plan_type: 'weekly' | 'monthly' | 'lifetime';
  days_active: number;
  reason?: string;
}) => {
  trackRybbitEvent('subscription_cancelled', {
    plan_type: params.plan_type,
    days_active: params.days_active,
    reason: params.reason,
  });
};

/**
 * Track hourly scan limit reached
 */
export const trackRybbitHourlyScanLimitReached = (params: {
  scans_used: number;
  time_until_reset_minutes: number;
}) => {
  trackRybbitEvent('hourly_scan_limit_reached', {
    scans_used: params.scans_used,
    time_until_reset_minutes: params.time_until_reset_minutes,
  });
};

/**
 * Track power user signup click (from limit modal)
 */
export const trackRybbitPowerUserSignupClick = () => {
  trackRybbitEvent('poweruser_signup_click');
};

/**
 * Track power user modal dismiss
 */
export const trackRybbitPowerUserDismiss = (params: {
  minutes_until_reset: number;
}) => {
  trackRybbitEvent('poweruser_dismiss', {
    minutes_until_reset: params.minutes_until_reset,
  });
};

/**
 * Track scan performed with remaining count
 */
export const trackRybbitScanPerformed = (params: {
  scans_remaining: number;
  scans_used: number;
}) => {
  trackRybbitEvent('scan_performed', {
    scans_remaining: params.scans_remaining,
    scans_used: params.scans_used,
  });
};

/**
 * Track signup from power user modal
 */
export const trackRybbitPowerUserSignupCompleted = () => {
  trackRybbitEvent('poweruser_signup_completed');
};

/**
 * Track pricing tier displayed to user on paywall
 */
export const trackRybbitPaywallTier = (params: {
  tier: number;
  country: string;
  weekly_price?: number;
  monthly_price?: number;
  lifetime_price?: number;
}) => {
  trackRybbitEvent(`paywall_tier${params.tier}`, {
    country: params.country,
    tier: params.tier,
    weekly_price: params.weekly_price,
    monthly_price: params.monthly_price,
    lifetime_price: params.lifetime_price,
  });
};

// =============================================================================
// PAYWALL FAQ TRACKING (5 goals)
// =============================================================================

/**
 * Track Paywall FAQ: What does 'Unlimited Characters' mean exactly?
 */
export const trackRybbitFaqUnlimitedCharacters = () => {
  trackRybbitEvent('faq_unlimited_characters');
};

/**
 * Track Paywall FAQ: Why should I choose Lifetime instead of monthly?
 */
export const trackRybbitFaqLifetimeVsSubscription = () => {
  trackRybbitEvent('faq_lifetime_vs_subscription');
};

/**
 * Track Paywall FAQ: What are the scan limits of the free version?
 */
export const trackRybbitFaqNoCooldown = () => {
  trackRybbitEvent('faq_no_cooldown');
};

/**
 * Track Paywall FAQ: What advantage does document upload offer?
 */
export const trackRybbitFaqDocumentUpload = () => {
  trackRybbitEvent('faq_document_upload');
};

/**
 * Track Paywall FAQ: How reliable is the watermark detection really?
 */
export const trackRybbitFaqDetectionAccuracy = () => {
  trackRybbitEvent('faq_detection_accuracy');
};

// =============================================================================
// EXIT-INTENT & SPECIAL OFFER FLOW (14 goals)
// =============================================================================

/**
 * Track paywall exit button click (Log out and continue for free)
 */
export const trackRybbitPaywallExitButtonClicked = (params: {
  user_tier: string;
  time_on_paywall_seconds: number;
}) => {
  trackRybbitEvent('paywall_exit_button_clicked', {
    user_tier: params.user_tier,
    time_on_paywall_seconds: params.time_on_paywall_seconds,
  });
};

/**
 * Track exit-intent modal shown
 */
export const trackRybbitExitIntentTriggered = (params: {
  user_tier: string;
  session_duration_minutes: number;
  scans_in_session: number;
  trigger_location: string;
}) => {
  trackRybbitEvent('exit_intent_triggered', {
    user_tier: params.user_tier,
    session_duration_minutes: params.session_duration_minutes,
    scans_in_session: params.scans_in_session,
    trigger_location: params.trigger_location,
  });
};

/**
 * Track exit-intent accepted (View Special Deal clicked)
 */
export const trackRybbitExitIntentAccepted = (params: {
  user_tier: string;
}) => {
  trackRybbitEvent('exit_intent_accepted', {
    user_tier: params.user_tier,
  });
};

/**
 * Track exit-intent declined
 */
export const trackRybbitExitIntentDeclined = () => {
  trackRybbitEvent('exit_intent_declined');
};

/**
 * Track special deal page viewed
 */
export const trackRybbitSpecialDealViewed = (params: {
  user_tier: string;
  discount_tier: number;
  weekly_discount: number;
  monthly_discount: number;
  lifetime_discount: number;
}) => {
  trackRybbitEvent('special_deal_viewed', {
    user_tier: params.user_tier,
    discount_tier: params.discount_tier,
    weekly_discount: params.weekly_discount,
    monthly_discount: params.monthly_discount,
    lifetime_discount: params.lifetime_discount,
  });
};

/**
 * Track special deal monthly plan clicked
 */
export const trackRybbitSpecialDealMonthlyClick = (params: {
  original_price: number;
  discounted_price: number;
  discount_percentage: number;
  savings: number;
}) => {
  trackRybbitEvent('special_deal_monthly_click', {
    original_price: params.original_price,
    discounted_price: params.discounted_price,
    discount_percentage: params.discount_percentage,
    savings: params.savings,
  });
};

/**
 * Track special deal lifetime plan clicked
 */
export const trackRybbitSpecialDealLifetimeClick = (params: {
  original_price: number;
  discounted_price: number;
  discount_percentage: number;
  savings: number;
}) => {
  trackRybbitEvent('special_deal_lifetime_click', {
    original_price: params.original_price,
    discounted_price: params.discounted_price,
    discount_percentage: params.discount_percentage,
    savings: params.savings,
  });
};

/**
 * Track special deal declined
 */
export const trackRybbitSpecialDealDeclined = () => {
  trackRybbitEvent('special_deal_declined');
};

/**
 * Track last chance modal triggered
 */
export const trackRybbitLastChanceTriggered = (params: {
  user_tier: string;
  discount_tier: number;
  lifetime_original_price: number;
  lifetime_final_price: number;
  lifetime_discount_percentage: number;
  lifetime_savings: number;
}) => {
  trackRybbitEvent('last_chance_triggered', {
    user_tier: params.user_tier,
    discount_tier: params.discount_tier,
    lifetime_original_price: params.lifetime_original_price,
    lifetime_final_price: params.lifetime_final_price,
    lifetime_discount_percentage: params.lifetime_discount_percentage,
    lifetime_savings: params.lifetime_savings,
  });
};

/**
 * Track last chance offer accepted
 */
export const trackRybbitLastChanceAccepted = (params: {
  original_price: number;
  final_price: number;
  discount_percentage: number;
  total_savings: number;
}) => {
  trackRybbitEvent('last_chance_accepted', {
    original_price: params.original_price,
    final_price: params.final_price,
    discount_percentage: params.discount_percentage,
    total_savings: params.total_savings,
  });
};

/**
 * Track last chance offer declined
 */
export const trackRybbitLastChanceDeclined = () => {
  trackRybbitEvent('last_chance_declined');
};

/**
 * Track purchase from exit-intent funnel
 */
export const trackRybbitExitIntentPurchase = (params: {
  transaction_id: string;
  plan_type: 'weekly' | 'monthly' | 'lifetime';
  value: number;
  currency: string;
  original_price: number;
  discount_percentage: number;
  savings: number;
  funnel_stage: 'special_deal' | 'last_chance';
}) => {
  trackRybbitEvent('exit_intent_purchase', {
    transaction_id: params.transaction_id,
    plan_type: params.plan_type,
    value: params.value,
    currency: params.currency,
    original_price: params.original_price,
    discount_percentage: params.discount_percentage,
    savings: params.savings,
    funnel_stage: params.funnel_stage,
  });
};

// =============================================================================
// CANCELLATION RETENTION FLOW
// =============================================================================

/**
 * Track cancellation modal shown
 */
export const trackRybbitCancellationModalShown = (params: {
  plan_type: string;
  days_active: number;
}) => {
  trackRybbitEvent('cancellation_modal_shown', {
    plan_type: params.plan_type,
    days_active: params.days_active,
  });
};

/**
 * Track cancellation offer accepted
 */
export const trackRybbitCancellationOfferAccepted = (params: {
  plan_type: string;
  offer_type: string;
}) => {
  trackRybbitEvent('cancellation_offer_accepted', {
    plan_type: params.plan_type,
    offer_type: params.offer_type,
  });
};

/**
 * Track cancellation offer declined
 */
export const trackRybbitCancellationOfferDeclined = (params: {
  plan_type: string;
}) => {
  trackRybbitEvent('cancellation_offer_declined', {
    plan_type: params.plan_type,
  });
};

/**
 * Identify a user (call when user logs in)
 */
export const identifyRybbitUser = (userId: string) => {
  if (typeof window === 'undefined' || !window.rybbit) {
    console.warn('[Rybbit] Rybbit is not initialized yet');
    return;
  }

  try {
    window.rybbit.identify(userId);
    if (import.meta.env.DEV) {
      console.log('[Rybbit] User identified:', userId);
    }
  } catch (error) {
    console.error('[Rybbit] Failed to identify user:', error);
  }
};

/**
 * Clear user identification (call when user logs out)
 */
export const clearRybbitUser = () => {
  if (typeof window === 'undefined' || !window.rybbit) {
    console.warn('[Rybbit] Rybbit is not initialized yet');
    return;
  }

  try {
    window.rybbit.clearUserId();
    if (import.meta.env.DEV) {
      console.log('[Rybbit] User identification cleared');
    }
  } catch (error) {
    console.error('[Rybbit] Failed to clear user identification:', error);
  }
};

// =============================================================================
// PURCHASE & REVENUE TRACKING
// =============================================================================

/**
 * Track successful purchase with revenue attribution
 *
 * This is the key conversion event that ties revenue back to traffic sources.
 * Attribution data (UTM, referrer) is automatically included.
 */
export const trackRybbitPurchase = (params: {
  transaction_id: string;
  plan_type: 'weekly' | 'monthly' | 'lifetime';
  value: number;
  currency: string;
  funnel_source?: 'standard' | 'exit_intent' | 'limit_upgrade' | 'signup_conversion';
  discount_percentage?: number;
  original_price?: number;
}) => {
  trackRybbitEvent('purchase', {
    transaction_id: params.transaction_id,
    plan_type: params.plan_type,
    value: params.value,
    currency: params.currency,
    funnel_source: params.funnel_source || 'standard',
    discount_percentage: params.discount_percentage || 0,
    original_price: params.original_price,
  });
};

/**
 * Track purchase event with retry logic for script loading
 *
 * Use this on the SuccessPage where the rybbit script might not be loaded yet
 * due to defer loading. Waits up to 5 seconds for the script to load.
 */
export const trackRybbitPurchaseWithRetry = async (params: {
  transaction_id: string;
  plan_type: 'weekly' | 'monthly' | 'lifetime';
  value: number;
  currency: string;
  funnel_source?: 'standard' | 'exit_intent' | 'limit_upgrade' | 'signup_conversion';
  discount_percentage?: number;
  original_price?: number;
}): Promise<boolean> => {
  const maxAttempts = 50; // 5 seconds with 100ms intervals

  for (let i = 0; i < maxAttempts; i++) {
    if (typeof window !== 'undefined' && window.rybbit) {
      trackRybbitPurchase(params);
      console.log('[Rybbit] Purchase event fired successfully after', i * 100, 'ms');
      return true;
    }
    await new Promise(r => setTimeout(r, 100));
  }

  console.error('[Rybbit] FAILED: Script never loaded after 5 seconds');
  return false;
};

/**
 * Export the core tracking function for advanced use cases
 * (e.g., funnel tracking module)
 */
export { trackRybbitEvent };
